<template>
  <div class="row g-0" v-bind:class="{ 'row-user-story': showActions }" v-if="localStory">
    <div class="col">
      <div
        class="user-story"
        v-bind:draggable="dragable"
        @dragstart="dragStart(story.id, $event)"
        @dragend="dragEnd($event)"
      >
        <div class="user-story-heading">
          <div class="row">
            <div class="col">
              <input type="checkbox" @click="$emit('selected', story.id)" v-if="selectable" />
              <router-link
                v-if="parentRouteName.length"
                tag="a"
                class="text-truncate"
                :to="{
                  name: routeName + '.preview',
                  params: {
                    project_id: $route.params.project_id,
                    stage_id: $route.params.stage_id,
                    user_story_doc_id: $route.params.user_story_doc_id,
                    sprint_id: $route.params.sprint_id,
                    story_id: story.id,
                  },
                }"
              >
                <p class="inline">US-{{ story.id }}</p>
              </router-link>
              <doc-tag :story="story" @open="$bus.$emit('us-doc-link', { storyId: story.id, userStoryDocId: $route.params.user_story_doc_id })" />
            </div>

            <div class="col-auto">
              <span class="status" v-bind:class="statusClass">{{ storyStatus }}</span>
              <div class="dropdown d-inline-block">
                <button type="button" v-dropdown class="btn btn-card-menu" aria-label="Actions"><i class="fal fa-ellipsis-v"></i></button>

                <div class="dropdown-menu" v-on:click.stop>
                  <a class="dropdown-item" href="#" @click="onDeleteClick">
                    <i class="fal fa-trash" style="margin-right: 5px"></i>
                    Delete
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="user-story-body">
          <div class="assign">
            <user-select :user="localStory.user" :userChanged="userChanged"></user-select>
          </div>

          <div class="right">
            <div class="title">
              <textarea
                style="width: 100%"
                ref="title"
                class="story-title-textarea form-control"
                v-auto-height
                v-if="edit.title"
                @blur="onFieldBlur('title')"
                v-model="localStory.title"
                @keyup="markDirty"
                placeholder="Title"
              ></textarea>
              <div v-else @click="onFieldClick('title')" v-html="getFormattedField('title')"></div>
            </div>
            <tag :value="localStory.tags" @input="handleSelectTagCallback" @tag="handleAddNewTag"></tag>
          </div>
        </div>

        <div class="user-story-footer">
          <div class="row">
            <div class="col">
              <div>
                <label class="visually-hidden">Type</label>
              </div>
            </div>

            <div class="col-auto">
              <ul class="timers">
                <li>
                  <span>E</span>
                  {{ story.quoted_estimate | toHoursMinutes }}
                </li>
                <li>
                  <span>S</span>
                  {{ story.time_entries_total | toHoursMinutes }}
                </li>
              </ul>
            </div>
          </div>
        </div>
        <!-- </div> -->
      </div>

      <div v-if="story.sprint_id && showActions" class="user-story-actions">
        <div class="d-inline-block float-start">
          <router-link
            v-if="parentRouteName.length"
            tag="a"
            class="btn"
            :to="{
              name: routeName + '.detail',
              params: {
                project_id: $route.params.project_id,
                stage_id: $route.params.stage_id,
                user_story_doc_id: $route.params.user_story_doc_id,
                sprint_id: $route.params.sprint_id,
                story_id: story.id,
              },
            }"
          >
            <i class="fa fa-expand-wide"></i>
            Detail
          </router-link>
        </div>

        <add-item :user-story-id="story.id" type="Issue"></add-item>

        <add-item :user-story-id="story.id" type="Task"></add-item>
      </div>
    </div>

    <template v-if="renderTasks">
      <div
        class="col droppable"
        @drop="dragFinish(story.id, 'New', $event)"
        @dragover.prevent
        @dragenter="dragEnter($event)"
        @dragleave="dragLeave($event)"
      >
        <item v-for="item in getByStoryIdAndStatus(story.id, 'New')" :key="item.id" :item="item"></item>
      </div>

      <div
        class="col droppable"
        @drop="dragFinish(story.id, 'In Progress', $event)"
        @dragover.prevent
        @dragenter="dragEnter($event)"
        @dragleave="dragLeave($event)"
      >
        <item v-for="item in getByStoryIdAndStatus(story.id, 'In Progress')" :key="item.id" :item="item"></item>
      </div>

      <div
        class="col droppable"
        @drop="dragFinish(story.id, 'Review', $event)"
        @dragover.prevent
        @dragenter="dragEnter($event)"
        @dragleave="dragLeave($event)"
      >
        <item v-for="item in getByStoryIdAndStatus(story.id, 'Review')" :key="item.id" :item="item"></item>
      </div>

      <div
        class="col droppable"
        @drop="dragFinish(story.id, 'Completed', $event)"
        @dragover.prevent
        @dragenter="dragEnter($event)"
        @dragleave="dragLeave($event)"
      >
        <item
          v-for="item in getByStoryIdAndStatus(story.id, ['Completed', 'Resolved'])"
          :key="item.id"
          :item="item"
        ></item>
      </div>
    </template>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import { filter, cloneDeep, debounce, clone } from 'lodash'

import addItem from './_add-item'
import item from './_item'
import DocTag from './DocTag'

import userstoryCommon from 'mixins/scrumboard/userstory-common'
import dragAndDrop from 'mixins/scrumboard/drag-and-drop'

export default {
  mixins: [dragAndDrop, userstoryCommon],
  props: {
    story: Object,
    renderTasks: Boolean,
    showActions: Boolean,
    selectable: Boolean,
    dragable: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    addItem,
    item,
    DocTag,
  },

  computed: {
    routeName() {
      //If ends in 'detail' or  'preview' then return without and it will re-append correct names.
      if (this.parentRouteName.indexOf('.detail') > -1) {
        return this.parentRouteName.replace('.detail', '')
      }

      if (this.parentRouteName.indexOf('.preview') > -1) {
        return this.parentRouteName.replace('.preview', '')
      }

      return this.parentRouteName
    },
  },

  watch: {
    story: function(newVal, oldVal) { // watch it
      this.localStory = cloneDeep(newVal)
    }
  },

  data: function () {
    return {
      parentRouteName: '',
      dragging: false,
      localStory: null
    }
  },

  mounted: function () {
    this.parentRouteName = cloneDeep(this.$route.name)
    this.localStory = cloneDeep(this.story)
  },

  methods: {
    dragStart(i, event) {
      this.dragging = true

      //Set userstory as focus in state
      //Set task as focus in state
      //this.$store.dispatch('yodizproject/stories/setSingle', this.getStoryById(this.item.story_id));
      //this.$store.dispatch('yodizproject/tasks/setSingle', this.getTaskById(this.item.id));

      event.dataTransfer.setData('text/plain', i)
      event.dataTransfer.dropEffect = 'move'
      event.target.style.opacity = 0.5
    },

    dragEnd(event) {
      event.target.style.opacity = ''
      this.dragging = false
    },
  },
}
</script>
